<script setup>
import FixPanel from "@/components/approval-center/partials/ui/FixPanel.vue";
import FixPanelHeaderTitle from "@/components/approval-center/partials/ui/FixPanelHeaderTitle.vue";
import FixPanelHeader from "@/components/approval-center/partials/ui/FixPanelHeader.vue";
import MoveGroupTransition from "@/components/transitions/MoveGroupTransition.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import FixContent from "@/components/approval-center/partials/ui/FixContent.vue";
import FixText from "@/components/approval-center/partials/ui/FixText.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import {ref} from "vue";
import ReviewStars from "@/components/approval-center/partials/ui/ReviewStars.vue";
import SuccessFlow from "@/components/approval-center/modals/SuccessFlow.vue";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";
import SingleTextDisapprovalFlow from "@/components/approval-center/modals/SingleTextDisapprovalFlow.vue";
import {useApprovals} from "@/composables/approval-center/useApprovals";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import LoadMoreButton from "@/components/approval-center/partials/buttons/LoadMoreButton.vue";
import FixPanelSubText from "@/components/approval-center/partials/ui/FixPanelSubText.vue";
import FixPanelFooter from "@/components/approval-center/partials/ui/FixPanelFooter.vue";

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
});

const {
  showSuccessFlow,
  showDisapprovalFlow,
  handleConfirmMutation,
  handleConfirmForDisapprovalMutation,
  handleClose,
  selectedApproval,
  selectApprovalForApprove,
  selectApprovalForDisapprove,
} = useApprovalFlow();

const {mutate: handleConfirm} = handleConfirmMutation();
const {mutate: handleConfirmForDisapproval} = handleConfirmForDisapprovalMutation();
const responseText = ref('');

const {
  approvals,
  isPending,
} = useApprovals(props.category);

const handleDisapprovalFlow = (approval) => {
  responseText.value = approval?.approvable?.output?.response;

  selectApprovalForDisapprove(approval);
};
</script>

<template>
  <FixPanel v-if="isPending || approvals.length">
    <FixPanelHeader class="tw-flex-col !tw-items-start">
      <FixPanelHeaderTitle>Google Reviews</FixPanelHeaderTitle>
      <FixPanelSubText>Here are my suggested replies for your latest reviews—feel free to edit or approve them.</FixPanelSubText>
    </FixPanelHeader>

    <LoadingBlock v-if="isPending" />

    <MoveGroupTransition
      v-else
      class="tw-space-y-8"
    >
      <FixPanel
        v-for="approval in approvals"
        :key="approval.id"
      >
        <div class="tw-flex lg:tw-flex-row tw-flex-col tw-gap-16 tw-justify-between tw-items-stretch">
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6">
            <FixPanelHeader class="tw-justify-between">
              <div class="tw-flex tw-items-center">
                <FixPanelIcon icon-path="icons/review" />
                <FixPanelHeaderTitle class="tw-ml-3">
                  {{ approval.approvable.input.review.reviewer_name }}
                </FixPanelHeaderTitle>
              </div>

              <ReviewStars :score="approval.approvable.input.review.star_rating" />
            </FixPanelHeader>

            <FixContent variant="secondary">
              <FixText>
                {{ approval.approvable.input.review.comment }}
              </FixText>
            </FixContent>
          </div>

          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6">
            <FixPanelHeader>
              <FixPanelIcon icon-path="icons/response" />
              <FixPanelHeaderTitle>Response</FixPanelHeaderTitle>
            </FixPanelHeader>

            <FixContent
              class="tw-flex tw-items-start tw-gap-8"
              :variant="!approval.approvable?.output?.response ? 'disabled' : 'primary'"
            >
              <KitIcon
                :state="!approval.approvable?.output?.response ? 'error' : 'default'"
              />

              <FixText>
                {{ approval.approvable.output.response || 'I don’t have quite enough details to respond to this one. Feel free to add your response.' }}
              </FixText>
            </FixContent>
          </div>
        </div>

        <FixPanelFooter
          :approval="approval"
          :category="category"
          @approve="selectApprovalForApprove(approval)"
          @revise="handleDisapprovalFlow(approval)"
        />
      </FixPanel>
    </MoveGroupTransition>
    <LoadMoreButton :category="category" />
  </FixPanel>

  <SuccessFlow
    v-if="showSuccessFlow"
    @confirm="handleConfirm({
      approval: selectedApproval,
      category: category,
    })"
    @close="handleClose"
  />

  <SingleTextDisapprovalFlow
    v-if="showDisapprovalFlow"
    v-model="responseText"
    @close="handleClose"
    @confirm="handleConfirmForDisapproval({
      approval: selectedApproval,
      newContent: responseText,
      category: category
    })"
  >
    <FixPanelHeader class="tw-justify-between">
      <div class="tw-flex tw-items-center">
        <FixPanelIcon icon-path="icons/review" />
        <FixPanelHeaderTitle class="tw-ml-3">
          {{ selectedApproval?.approvable?.input?.review?.reviewer_name }}
        </FixPanelHeaderTitle>
      </div>

      <ReviewStars
        class="tw-text-white"
        :score="selectedApproval?.approvable?.input?.review?.star_rating"
      />
    </FixPanelHeader>

    <FixContent variant="disabled">
      <FixText>
        {{ selectedApproval?.approvable?.input?.review?.comment }}
      </FixText>
    </FixContent>

    <template #label>
      <FixPanelIcon icon-path="icons/response" />
      Response
    </template>
  </SingleTextDisapprovalFlow>
</template>
